import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, ListTodo } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { TaskCard } from '@/features/tasks/components/TaskCard';
import { TaskForm } from '@/features/tasks/components/TaskForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';

export function TasksPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  const { data: tasks, isLoading, error, refetch } = useQuery({
    queryKey: ['tasks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task')
        .select('id, title, status, priority, due_date')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const handleCreate = async (values: { title: string; description?: string; status: string; priority: string; due_date?: string }) => {
    await supabase.from('task').insert({
      title: values.title,
      description: values.description ?? '',
      status: values.status,
      priority: values.priority,
      due_date: values.due_date || null,
    } as never);
    setShowCreate(false);
    refetch();
  };

  return (
    <div>
      <PageHeader
        title="Tasks"
        action={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Task
          </Button>
        }
      />

      {error && <ErrorAlert message={String(error)} onRetry={() => refetch()} />}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      )}

      {!isLoading && tasks?.length === 0 && (
        <EmptyState
          icon={ListTodo}
          title="No tasks yet"
          description="Create your first task to get started."
          actionLabel="Create Task"
          onAction={() => setShowCreate(true)}
        />
      )}

      {tasks && tasks.length > 0 && (
        <div className="space-y-3">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} onClick={() => navigate(`/tasks/${task.id}`)} />
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <TaskForm onSubmit={handleCreate} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
